import { orderBy } from 'lodash';
import { Subject, Observable, of, NEVER } from 'rxjs';

import { SnTip } from '@lib/api/entities';
import { SnCollection } from '@lib/common/models';
import { SnSmartBadgeService } from '@lib/ui/badge';

export class SnTipsTracking {
  private readonly _tipMap = new SnCollection<SnTip>();

  private _tipList: SnTip[] = [];
  private _tipCount = 0;

  private readonly _tipCreated$ = new Subject<SnTip>();
  private readonly _tipUpdated$ = new Subject<SnTip>();
  private readonly _tipRemoved$ = new Subject<SnTip>();

  constructor(private readonly _smartBadge: SnSmartBadgeService) {}

  get tipCount(): number {
    return this._tipCount;
  }

  get tipList(): SnTip[] {
    return this._tipList;
  }

  get tipCreated$(): Observable<SnTip> {
    return this._tipCreated$.asObservable();
  }

  get tipUpdated$(): Observable<SnTip> {
    return this._tipUpdated$.asObservable();
  }

  get tipRemoved$(): Observable<SnTip> {
    return this._tipRemoved$.asObservable();
  }

  public getTip(id: string): Observable<SnTip> {
    if (!this._tipMap.has(id)) {
      return NEVER;
    }

    return of(this._tipMap.get(id));
  }

  public addTip(data: any, emitEvent: boolean = true): void {
    if (this._tipMap.has(data.id)) {
      this.updateTip(data.id, data);
    } else {
      const tip = new SnTip(data);
      this._tipMap.setUp(tip);
      this._updateTipList();

      if (emitEvent) {
        this._tipCreated$.next(tip);
      }
    }
  }

  public removeTip(id: string): void {
    this.getTip(id).subscribe((tip) => {
      this._tipMap.delete(tip);
      this._updateTipList();
      this._tipRemoved$.next(tip);
    });
  }

  public updateTip(id: string, data: any): void {
    const tip = this._tipMap.get(id);
    tip.fromJSON(data);
    this._updateTipList();
    this._tipUpdated$.next(tip);
  }

  public clear(): void {
    this._tipMap.clear();
    this._updateTipList();
  }

  public destroy(): void {
    this._tipMap.clear();

    this._tipCreated$.complete();
    this._tipUpdated$.complete();
    this._tipRemoved$.complete();
  }

  private _updateTipList(): void {
    this._tipList = Array.from(this._tipMap.values());
    this._tipList = orderBy(this._tipList, 'updatedAt', 'desc');
    this._tipCount = this._tipList.length;

    this._smartBadge.setBadge('live-tracking__tips-count', this._tipCount);
  }
}
